import React from 'react';
import { Link } from 'react-router-dom';
import { useKioskMode } from '../../contexts/KioskModeContext';
import { useKioskGuestOrderingPath } from '../Kiosk/useKioskGuestOrderingPath';
import './OrderUnavailableState.css';

export type OrderUnavailableVariant = 'access' | 'not_found' | 'generic';

export function classifyOrderUnavailable(message: string): OrderUnavailableVariant {
  const m = message.toLowerCase();
  if (m.includes("don't have access") || m.includes('do not have access')) {
    return 'access';
  }
  if (m.includes('not found')) {
    return 'not_found';
  }
  return 'generic';
}

interface OrderUnavailableStateProps {
  appName?: string;
  message?: string;
  variant?: OrderUnavailableVariant;
}

const copyByVariant: Record<
  OrderUnavailableVariant,
  { icon: string; title: string; hint: string }
> = {
  access: {
    icon: 'fa-lock',
    title: "This order isn't on your account",
    hint: 'The link may be wrong, or the order was placed under a different sign-in. Check Your Orders or browse the menu to start a new one.',
  },
  not_found: {
    icon: 'fa-search',
    title: 'Order not found',
    hint: "We couldn't find an order with this number. It may have been removed or the URL is incorrect.",
  },
  generic: {
    icon: 'fa-exclamation-circle',
    title: 'Unable to load order',
    hint: 'Something went wrong while fetching this order. Please try again in a moment.',
  },
};

const OrderUnavailableState: React.FC<OrderUnavailableStateProps> = ({
  appName,
  message,
  variant: variantProp,
}) => {
  const { enabled: kioskShell } = useKioskMode();
  const guestOrderingPath = useKioskGuestOrderingPath();
  const variant = variantProp ?? classifyOrderUnavailable(message || '');
  const copy = copyByVariant[variant];
  const menuPath = appName
    ? guestOrderingPath(`/restaurants/${appName}/menu`)
    : kioskShell
      ? '/'
      : '/restaurants';
  const ordersPath = appName
    ? guestOrderingPath(`/restaurants/${appName}/my-orders`)
    : '/my-orders';

  return (
    <div className="order-unavailable" role="alert">
      <div className="order-unavailable__icon-wrap" aria-hidden>
        <i className={`fa ${copy.icon}`} />
      </div>
      <h2 className="order-unavailable__title">{copy.title}</h2>
      <p className="order-unavailable__hint">{copy.hint}</p>
      {message && variant === 'generic' && (
        <p className="order-unavailable__detail">{message}</p>
      )}
      <div className="order-unavailable__actions">
        <Link to={ordersPath} className="order-unavailable__btn order-unavailable__btn--primary">
          <i className="fa fa-list-alt" aria-hidden />
          Your Orders
        </Link>
        <Link to={menuPath} className="order-unavailable__btn order-unavailable__btn--secondary">
          Browse Menu
        </Link>
      </div>
    </div>
  );
};

export default OrderUnavailableState;
